import React, { useState, useEffect, useMemo } from 'react';
import {
    MdCheckCircle,
    MdRadioButtonUnchecked,
    MdSearch,
    MdSave,
    MdSettings,
    MdInfo,
    MdStar,
    MdWork,
    MdKeyboardArrowDown
} from 'react-icons/md';

import { usersAPI, servicesAPI, api } from 'services/api';

// --- YARDIMCI FONKSİYON ---
const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

// --- SKELETON LOADER (Ana Sayfa İçin) ---
const MainSkeleton = () => (
    <div className="animate-pulse space-y-6 mt-4">
        <div className="h-32 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        <div className="h-16 w-full rounded-[16px] bg-gray-200 dark:bg-navy-700" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
                <div key={i} className="h-24 w-full rounded-[16px] bg-gray-200 dark:bg-navy-700" />
            ))}
        </div>
    </div>
);

// --- SKELETON LOADER (Hizmetler İçin) ---
const ServicesSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-pulse">
        {[...Array(8)].map((_, i) => (
            <div key={i} className="h-24 w-full rounded-[16px] bg-gray-100 dark:bg-navy-900/50" />
        ))}
    </div>
);

const AdminStaffServiceAssignments = () => {
    // --- STATE (WORK-LOGIC BİREBİR KORUNDU) ---
    const [loading, setLoading] = useState(true);
    const [fetchingStaffData, setFetchingStaffData] = useState(false);
    const [saving, setSaving] = useState(false);

    const [staffList, setStaffList] = useState([]);
    const [allServices, setAllServices] = useState([]);

    const [selectedStaffId, setSelectedStaffId] = useState('');
    const [assignedServiceIds, setAssignedServiceIds] = useState([]);
    const [genderFilter, setGenderFilter] = useState('all'); // 'all', 'male', 'female'
    const [isDefaultAll, setIsDefaultAll] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 12;

    // --- 1. SAYFA YÜKLENİRKEN GENEL VERİLERİ ÇEK ---
    useEffect(() => {
        let mounted = true;
        const initData = async () => {
            try {
                setLoading(true);
                const [staffRes, servicesRes] = await Promise.all([
                    usersAPI.getStaff(),
                    servicesAPI.getAll()
                ]);

                if (mounted) {
                    if (staffRes.success || staffRes.staff) setStaffList(staffRes.staff || []);
                    if (servicesRes.success || servicesRes.services) setAllServices(servicesRes.services || []);
                }
            } catch (err) {
                console.error("Init Error:", err);
                if (mounted) setMessage({ type: 'danger', text: 'Başlangıç verileri yüklenirken hata oluştu.' });
            } finally {
                if (mounted) setLoading(false);
            }
        };
        initData();
        return () => { mounted = false; };
    }, []);

    // --- 2. PERSONEL SEÇİMİ DEĞİŞTİĞİNDE ---
    useEffect(() => {
        setAssignedServiceIds([]);
        setIsDefaultAll(false);
        setMessage({ type: '', text: '' });
        setCurrentPage(1); // Personel değiştiğinde 1. sayfaya dön

        if (!selectedStaffId) return;

        const fetchStaffServices = async () => {
            setFetchingStaffData(true);
            try {
                const response = await api.get(`/services?staff_id=${selectedStaffId}`);
                if (response.success || response.services) {
                    const fetchedServices = response.services || [];
                    const fetchedIds = fetchedServices.map(s => s.id);

                    setAssignedServiceIds(fetchedIds);

                    if (allServices.length > 0 && fetchedIds.length === allServices.length) {
                        setIsDefaultAll(true);
                    } else {
                        setIsDefaultAll(false);
                    }
                }
            } catch (err) {
                console.error("Staff Fetch Error:", err);
                setMessage({ type: 'danger', text: 'Personel yetkileri getirilemedi.' });
            } finally {
                setFetchingStaffData(false);
            }
        };

        if (allServices.length > 0) {
            fetchStaffServices();
        }
    }, [selectedStaffId, allServices.length]);

    // Filtreler değiştiğinde sayfayı başa al
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, genderFilter]);

    // --- İŞLEMLER ---
    const handleToggleService = (serviceId) => {
        if (isDefaultAll) setIsDefaultAll(false);
        setAssignedServiceIds(prev => {
            if (prev.includes(serviceId)) return prev.filter(id => id !== serviceId);
            return [...prev, serviceId];
        });
    };

    const handleSelectAll = () => {
        const allIds = allServices.map(s => s.id);
        setAssignedServiceIds(allIds);
        setIsDefaultAll(false);
    };

    const handleDeselectAll = () => {
        setAssignedServiceIds([]);
        setIsDefaultAll(false);
    };

    const handleSaveChanges = async () => {
        if (!selectedStaffId) return;
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            await usersAPI.assignServices(selectedStaffId, assignedServiceIds);
            setMessage({ type: 'success', text: 'Hizmet atamaları başarıyla güncellendi.' });
            setTimeout(() => setMessage({ type: '', text: '' }), 4000);
        } catch (err) {
            console.error("Save Error:", err);
            setMessage({ type: 'danger', text: 'Kaydetme başarısız oldu.' });
        } finally {
            setSaving(false);
        }
    };

    // --- FİLTRELEME & SIRALAMA ---
    const filteredServices = useMemo(() => {
        return allServices
            .filter(service => {
                const matchSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase());
                let matchGender = true;
                if (genderFilter === 'male') {
                    matchGender = service.target_gender === 'male' || service.target_gender === 'unisex' || !service.target_gender;
                } else if (genderFilter === 'female') {
                    matchGender = service.target_gender === 'female' || service.target_gender === 'unisex' || !service.target_gender;
                }
                return matchSearch && matchGender;
            })
            .sort((a, b) => {
                const aFav = (a.is_favorite === true || a.is_favorite === 1) ? 1 : 0;
                const bFav = (b.is_favorite === true || b.is_favorite === 1) ? 1 : 0;
                return bFav - aFav; // Favorileri en üste al
            });
    }, [allServices, searchTerm, genderFilter]);

    // Pagination İşlemleri
    const totalPages = Math.ceil(filteredServices.length / PAGE_SIZE);
    const currentServices = filteredServices.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const selectedStaffDetails = staffList.find(s => s.id == selectedStaffId);

    // --- RENDER ---
    if (loading) return <MainSkeleton />;

    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-28">

            {/* 1. ÜST KART: BAŞLIK VE PERSONEL SEÇİMİ */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 rounded-[20px] bg-white p-6 shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">

                {/* Başlık */}
                <div className="flex items-center gap-4 w-full lg:w-auto">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
                        <MdSettings className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">Hizmet Yetkilendirme</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Personelin sisteme erişim ve hizmet yetkilerini yönetin.
                        </p>
                    </div>
                </div>

                {/* Personel Seçimi (Modern Dropdown UI) */}
                <div className="relative w-full lg:w-96">
                    <select
                        value={selectedStaffId}
                        onChange={(e) => setSelectedStaffId(e.target.value)}
                        className="w-full h-14 appearance-none rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all cursor-pointer"
                    >
                        <option value="" disabled>Yönetilecek personeli seçin...</option>
                        {staffList.map(staff => (
                            <option key={staff.id} value={staff.id}>
                                {staff.name} {staff.surname}
                            </option>
                        ))}
                    </select>
                    <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-white">
                        <MdKeyboardArrowDown className="text-2xl" />
                    </div>
                </div>
            </div>

            {/* BİLDİRİMLER (Toast Message) */}
            {message.text && (
                <div className={`flex items-center gap-3 p-4 rounded-xl border ${message.type === 'danger' ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400' : 'bg-green-50 border-green-200 text-green-600 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-400'} animate-fadeIn`}>
                    <MdInfo className="text-xl flex-none" />
                    <span className="text-sm font-bold">{message.text}</span>
                </div>
            )}

            {/* İÇERİK ALANI */}
            {selectedStaffId ? (
                <div className="flex flex-col gap-6 animate-fadeIn">

                    {/* TAM ERİŞİM UYARISI */}
                    {isDefaultAll && !fetchingStaffData && (
                        <div className="flex items-center gap-3 rounded-xl bg-blue-50 p-4 border border-blue-200 text-blue-700 dark:bg-blue-500/10 dark:border-blue-500/20 dark:text-blue-400">
                            <MdInfo className="text-2xl flex-none" />
                            <div>
                                <strong className="block text-sm font-bold">Tam Erişim Modu</strong>
                                <span className="text-xs font-medium mt-0.5 block">Bu personele özel bir kısıtlama tanımlanmamış. Sistem varsayılan olarak tüm hizmetleri yapabilir kabul eder.</span>
                            </div>
                        </div>
                    )}

                    {/* ARAÇ ÇUBUĞU (Arama, Filtreler, Seçim Butonları) */}
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-4 bg-white dark:bg-navy-800 p-4 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10">

                        {/* Arama ve Toplu İşlem */}
                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
                            <div className="relative w-full sm:w-64">
                                <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                                <input
                                    type="text"
                                    placeholder="Hizmetlerde ara..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full h-11 pl-10 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all"
                                />
                            </div>
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                <button onClick={handleSelectAll} className="flex-1 sm:flex-none h-11 px-4 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-900 text-sm font-bold text-navy-700 dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 transition-all">
                                    Tümünü Seç
                                </button>
                                <button onClick={handleDeselectAll} className="flex-1 sm:flex-none h-11 px-4 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-900 text-sm font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all">
                                    Temizle
                                </button>
                            </div>
                        </div>

                        {/* Cinsiyet Filtresi (Pill Style) */}
                        <div className="flex w-full sm:w-auto bg-gray-100 dark:bg-navy-900 rounded-xl p-1.5">
                            {[
                                { id: 'all', label: 'Tümü' },
                                { id: 'female', label: 'Kadın' },
                                { id: 'male', label: 'Erkek' }
                            ].map(f => (
                                <button
                                    key={f.id}
                                    onClick={() => setGenderFilter(f.id)}
                                    className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                                        genderFilter === f.id
                                            ? 'bg-white text-brand-500 shadow-sm dark:bg-navy-800 dark:text-brand-400'
                                            : 'text-gray-500 hover:text-navy-700 dark:hover:text-white'
                                    }`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* HİZMETLER LİSTESİ VEYA SKELETON */}
                    {fetchingStaffData ? (
                        <ServicesSkeleton />
                    ) : (
                        <>
                            {filteredServices.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 rounded-[20px] border border-dashed border-gray-300 dark:border-white/10 bg-white/50 dark:bg-navy-800/50">
                                    <MdWork className="text-5xl text-gray-300 dark:text-gray-600 mb-3" />
                                    <p className="text-navy-700 dark:text-white font-bold text-lg">Hizmet Bulunamadı</p>
                                    <p className="text-sm text-gray-500 mt-1">Arama kriterlerinize uygun bir hizmet eşleşmedi.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {currentServices.map(service => {
                                        const isAssigned = assignedServiceIds.includes(service.id);
                                        const isFav = service.is_favorite === true || service.is_favorite === 1;

                                        // HorizonUI Cinsiyet Renk Dinamikleri
                                        let activeBgClass = 'bg-brand-50 dark:bg-brand-400/10';
                                        let activeBorderClass = 'border-brand-500 dark:border-brand-400';
                                        let iconColorClass = 'text-brand-500 dark:text-brand-400';

                                        if (service.target_gender === 'male') {
                                            activeBgClass = 'bg-blue-50 dark:bg-blue-500/10';
                                            activeBorderClass = 'border-blue-500 dark:border-blue-400';
                                            iconColorClass = 'text-blue-500 dark:text-blue-400';
                                        } else if (service.target_gender === 'female') {
                                            activeBgClass = 'bg-pink-50 dark:bg-pink-500/10';
                                            activeBorderClass = 'border-pink-500 dark:border-pink-400';
                                            iconColorClass = 'text-pink-500 dark:text-pink-400';
                                        }

                                        return (
                                            <div
                                                key={service.id}
                                                onClick={() => handleToggleService(service.id)}
                                                className={`relative flex flex-col justify-between p-4 rounded-[16px] cursor-pointer transition-all duration-200 border-2 ${
                                                    isAssigned
                                                        ? `${activeBgClass} ${activeBorderClass} shadow-md`
                                                        : 'bg-white dark:bg-navy-800 border-transparent hover:border-gray-200 dark:hover:border-white/10 shadow-sm border border-gray-100 dark:border-white/5'
                                                }`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    {/* İkon */}
                                                    <div className={`flex-none w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                                        isAssigned ? 'bg-white dark:bg-navy-900 shadow-sm' : 'bg-gray-50 dark:bg-navy-900'
                                                    }`}>
                                                        {isAssigned ? (
                                                            <MdCheckCircle className={`text-xl ${iconColorClass}`} />
                                                        ) : (
                                                            <MdRadioButtonUnchecked className="text-xl text-gray-300 dark:text-gray-500" />
                                                        )}
                                                    </div>

                                                    {/* İsim ve Rozet */}
                                                    <div className="flex-1 mt-0.5">
                                                        <h6 className={`font-bold text-sm leading-tight line-clamp-2 ${isAssigned ? 'text-navy-700 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                                                            {service.name}
                                                        </h6>
                                                        {isFav && (
                                                            <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded bg-amber-50 text-[10px] font-bold text-amber-600 dark:bg-amber-500/10 dark:text-amber-400">
                                <MdStar /> Popüler
                              </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Alt Bilgi (Süre & Fiyat) */}
                                                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-white/5">
                                                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{service.duration} Dk</span>
                                                    <span className="text-xs font-bold bg-navy-700 text-white dark:bg-white dark:text-navy-700 px-2 py-1 rounded-md">
                            {service.price} ₺
                          </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* SAYFALAMA (Pagination) */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100 dark:border-white/10">
                                    <p className="text-sm font-medium text-gray-500">
                                        Toplam <span className="font-bold text-navy-700 dark:text-white">{filteredServices.length}</span> hizmet. Sayfa: {currentPage}/{totalPages}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-800 text-sm font-bold text-navy-700 dark:text-white disabled:opacity-40 transition-all hover:bg-gray-50 dark:hover:bg-navy-700"
                                        >
                                            Önceki
                                        </button>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-800 text-sm font-bold text-navy-700 dark:text-white disabled:opacity-40 transition-all hover:bg-gray-50 dark:hover:bg-navy-700"
                                        >
                                            Sonraki
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* FLOATING SAVE BAR (Yüzen Kaydet Çubuğu) */}
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[90%] max-w-2xl z-50 animate-slideUp">
                        <div className="flex items-center justify-between p-4 rounded-[20px] bg-white/80 dark:bg-navy-900/90 backdrop-blur-md shadow-[0_10px_40px_rgba(0,0,0,0.1)] dark:shadow-[0_10px_40px_rgba(0,0,0,0.5)] border border-gray-100 dark:border-white/10">

                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-sm shadow-md">
                                    {selectedStaffDetails?.profile_picture ? (
                                        <img src={selectedStaffDetails.profile_picture} alt="profil" className="h-full w-full rounded-full object-cover" />
                                    ) : (
                                        getInitials(selectedStaffDetails?.name)
                                    )}
                                </div>
                                <div>
                                    <h6 className="text-sm font-bold text-navy-700 dark:text-white leading-tight">
                                        {selectedStaffDetails?.name} {selectedStaffDetails?.surname}
                                    </h6>
                                    <p className={`text-[10px] font-bold mt-0.5 ${assignedServiceIds.length === 0 ? 'text-red-500' : 'text-green-500'}`}>
                                        {assignedServiceIds.length} HİZMET TANIMLI
                                    </p>
                                </div>
                            </div>

                            <button
                                onClick={handleSaveChanges}
                                disabled={saving || fetchingStaffData}
                                className="flex items-center gap-2 h-10 px-6 rounded-xl bg-brand-500 text-sm font-bold text-white shadow-lg shadow-brand-500/30 hover:bg-brand-600 disabled:opacity-60 transition-all active:scale-95"
                            >
                                {saving ? (
                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <><MdSave className="text-lg" /> Kaydet</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Yüzen bar animasyonu için CSS */}
                    <style jsx="true">{`
            @keyframes slideUp {
              from { opacity: 0; transform: translate(-50%, 40px); }
              to { opacity: 1; transform: translate(-50%, 0); }
            }
            .animate-slideUp { animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
          `}</style>
                </div>
            ) : (
                /* PERSONEL SEÇİLMEDİ BOŞ EKRANI */
                <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5">
                    <div className="h-24 w-24 rounded-full bg-gray-50 dark:bg-navy-900 border border-gray-100 dark:border-white/5 flex items-center justify-center mb-4">
                        <MdSettings className="text-4xl text-gray-300 dark:text-gray-600" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-700 dark:text-white">Seçim Bekleniyor</h4>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-2 max-w-sm text-center">
                        İşlem yapmak için lütfen üst kısımdan yetkilerini düzenlemek istediğiniz personeli seçiniz.
                    </p>
                </div>
            )}
        </div>
    );
};

export default AdminStaffServiceAssignments;